<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faBagShopping,
    faBell,
    faBellPlus,
} from '@fortawesome/pro-regular-svg-icons';
import CircleWaves from '@components/ui/hotelPage&view/CircleWaves.vue';
import { notifyTrack } from '@composable/usePriceTrackModal';
import { MrSwitch } from '#components';
import type TripModule from '@itinerary/trip/TripModule';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import { TrackingType } from '@itinerary/types';

interface IProps {
    inner?: boolean;
    mobileOnly?: boolean;
    desktopOnly?: boolean;
    trackingType: TrackingType;
}

const { inner, trackingType } = defineProps<IProps>();

const emit = defineEmits<{
    'toggle:restart': [];
}>();

const priceTrackStore = usePriceTrackStore();
const analyticsStore = useAnalyticsStore();
const switchLoading = ref(false);
const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
const { originCode, destinationCode, departureDate } = moduleToRefs(
    currentSearchForm.value
);
const currentModule = useCurrentItineraryModule<TripModule>();
const { originCurrentCode, destinationCurrentCode } = moduleToRefs(
    currentModule.value
);
const toggle = ref<InstanceType<typeof MrSwitch>>();
const rootEl = ref<HTMLDivElement>();
const trackingTypes = computed<
    | {
          icon: IconDefinition;
          iconColor?: string;
          title?: string;
          description?: string;
      }
    | undefined
>(() => {
    switch (trackingType) {
        case TrackingType.Presell:
            return {
                title: 'اطلاع از پیش‌فروش قطار',
                icon: faBagShopping,
            };
        case TrackingType.Capacity:
            return {
                title: 'موجود شد خبرم کن',
                icon: faBellPlus,
                iconColor: 'var(--blue-400)',
            };
        case TrackingType.Price:
        default:
            return {
                title: 'ارزان شد خبرم کن',
                icon: faBell,
                iconColor: 'var(--blue-400)',
            };
    }
});
const selected = computed(() => !!priceTrack.value);

const switchAction = async () => {
    switchLoading.value = true;
    try {
        await addPriceTrack();
    } catch {}
    nextTick(() => {
        switchLoading.value = false;
    });
};
const priceTrack = computed(() => {
    if (trackingType === TrackingType.Presell) {
        const prevList = priceTrackStore.trainPreSellAlerts;
        return prevList.includes(
            `${originCode.value}-${destinationCode.value}`
        );
    } else {
        return priceTrackStore.priceTrackList.find(
            p =>
                !p.serviceId &&
                !p.serviceNumber &&
                p.originCode === originCurrentCode?.value &&
                p.destinationCode === destinationCurrentCode.value &&
                p.notify &&
                p.date &&
                departureDate.value &&
                dayConfig.gregory(p.date).isSame(departureDate.value, 'day')
        );
    }
});
const addPriceTrack = async () => {
    analyticsStore.trackPosthog(
        `click-on-add-price-track-${priceTrackStore.mapType(trackingType)}`
    );
    await notifyTrack({
        type: trackingType ?? undefined,
    }).then(() => restartToggle());
};
const restartToggle = () => {
    toggle.value?.restart();
    emit('toggle:restart');
};
</script>

<template>
    <div ref="rootEl" class="w-full">
        <CircleWaves class="waves" to-scale="scale(1.2, 1.5)" />
        <div class="price-track" :class="[{ selected, inner }]">
            <div class="root">
                <mr-icon
                    v-if="trackingTypes"
                    class="icon"
                    :icon="trackingTypes.icon"
                    :color="trackingTypes.iconColor"
                />
                <div
                    class="title"
                    :class="{ described: !!trackingTypes?.description }"
                >
                    {{ trackingTypes?.title }}
                </div>
                <p v-if="trackingTypes?.description" class="desktop-only">
                    {{ trackingTypes.description }}
                </p>
                <mr-loading
                    v-if="switchLoading"
                    class="switch-loading"
                    :scale="0.7"
                />
                <mr-switch
                    v-else
                    ref="toggle"
                    v-model="selected"
                    size="big"
                    @update:model-value="switchAction"
                    @click.stop
                />
            </div>
            <p v-if="trackingTypes?.description" class="mobile-only">
                {{ trackingTypes.description }}
            </p>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.w-full {
    width: 100%;
    position: relative;
}

.waves {
    display: none;
}
.price-track {
    width: 100%;
    border-radius: 12px;
    @include shadow();
    padding: 12px 16px;
    z-index: 100;
    background-color: white;
    &.inner {
        @include shadow(0);
    }
    > p {
        margin-top: 16px;
        margin-bottom: 0;
        @include tg-s($w5);
    }
    .root {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        width: 100%;
        height: 100%;
        @include mobile-only() {
            flex-wrap: nowrap;
        }
        :deep(.switch-loading) {
            padding: 0;
            width: 30px;
        }

        .title {
            @include tg-m($w7, usePalette());
            white-space: nowrap;
            margin: 0 8px 0 12px;
            flex-grow: 1;
            &.icon {
                flex-grow: unset;
            }
            &.described {
                @include desktop-only {
                    flex-grow: unset;
                }
            }
        }
        p {
            @include tg-s($w5);
            flex-grow: 1;
            margin: 0;
            &.desktop {
                visibility: visible;
                @include mobile-only {
                    visibility: hidden;
                    height: 0;
                }
            }
        }
    }
    &.notifier {
        span,
        p {
            color: usePalette($p-purple, $p-700);
        }
    }
}
</style>
